/** @jsx jsx */
import React, { Component, type ComponentType } from 'react';
import { css, jsx } from '@compiled/react';
import Button from '@atlaskit/button';
import { RequiredAsterisk } from '@atlaskit/form';
import { ModalBody, ModalFooter } from '@atlaskit/modal-dialog';
import { token } from '@atlaskit/tokens';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import type { FlagService } from '@atlassian/jira-flags';
import type { IntlShape } from '@atlassian/jira-intl';
import { JiraModal as ModalDialog } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import type { NavigationItem } from '@atlassian/jira-navigation-types/src/types.tsx';
import type { RefObject } from '@atlassian/jira-shared-types/src/general.tsx';
import { LINK_TYPE } from '../../common/const.tsx';
import { successFlag, errorFlag } from '../flags/index.tsx';
import addLink from '../services/add-link/index.tsx';
import messages from './messages.tsx';

type ViewState = {
	url: string;
	name: string;
	hasError: boolean;
	isInProgress: boolean;
};

type ViewProps = {
	projectId: string;
	// eslint-disable-next-line jira/deprecations/no-base-url
	baseUrl: string;
	Content: ComponentType<{
		onDataChange: (arg1: { url: string; name: string; hasError?: boolean }) => void;
	}>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	Header: ComponentType<any>;
	flagService: FlagService;
	onCompleted: (arg1: { item: NavigationItem | undefined }) => void;
	intl: IntlShape;
	addItemButtonRef?: RefObject;
};
// eslint-disable-next-line jira/react/no-class-components
class AddLinkModal extends Component<ViewProps, ViewState> {
	state = {
		url: '',
		name: '',
		hasError: false,
		isInProgress: false,
	};

	onDataChange = ({ url, name, hasError }: { url: string; name: string; hasError?: boolean }) => {
		// @ts-expect-error - TS2322 - Type 'boolean | undefined' is not assignable to type 'boolean'.
		this.setState({ url, name, hasError });
	};

	onAdd = () => {
		this.performAdd();
	};

	onClose = () => {
		const { onCompleted } = this.props;
		onCompleted({ item: undefined });
	};

	async performAdd() {
		// eslint-disable-next-line jira/deprecations/no-base-url
		const { projectId, baseUrl, onCompleted, flagService } = this.props;

		const { name, url } = this.state;
		try {
			this.setState({ isInProgress: true });
			// eslint-disable-next-line jira/deprecations/no-base-url
			const { id } = await addLink(baseUrl, {
				projectId,
				name,
				url,
				icon: '',
				type: 'project.shortcut.default.link',
			});
			this.setState({ isInProgress: false });

			onCompleted({
				item: {
					type: 'Item',
					id,
					text: name,
					href: url,
					shortcutType: LINK_TYPE,
				},
			});
			flagService.showFlag(successFlag());
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (e: any) {
			flagService.showFlag(errorFlag());
			onCompleted({ item: undefined });
		}
	}

	render() {
		const { url, name, hasError } = this.state;
		const isSubmitEnabled = !!url.trim() && !!name.trim() && !hasError;
		const { Content, Header } = this.props;
		const { isInProgress } = this.state;
		const {
			intl: { formatMessage },
			addItemButtonRef,
		} = this.props;

		return (
			<ShortcutScope>
				<ModalDialog
					width="medium"
					onClose={this.onClose}
					autoFocus={false}
					shouldReturnFocus={addItemButtonRef}
					shouldCloseOnOverlayClick={false}
					messageId="navigation-shortcuts.add-link-modal.modal-dialog"
					messageType="transactional"
				>
					<Header />
					<ModalBody>
						{/* eslint-disable-next-line @atlaskit/design-system/use-heading */}
						<h1 css={headingStyles}>{formatMessage(messages.heading)}</h1>
						{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
						<p aria-hidden="true" css={requiredTextStyles}>
							{formatMessage(messages.requiredField)}
							<RequiredAsterisk />
						</p>
						<Content onDataChange={this.onDataChange} />
					</ModalBody>
					<ModalFooter>
						<Button appearance="link" onClick={this.onClose} isDisabled={isInProgress}>
							{formatMessage(messages.cancelCaption)}
						</Button>
						<Button
							appearance="primary"
							onClick={this.onAdd}
							isDisabled={!isSubmitEnabled || isInProgress}
						>
							{formatMessage(messages.submitCaption)}
						</Button>
					</ModalFooter>
				</ModalDialog>
			</ShortcutScope>
		);
	}
}

export default AddLinkModal;

const headingStyles = css({ marginTop: 0 });
const requiredTextStyles = css({ color: token('color.text.subtle') });
